/**
 * Custom Auth Client for Platform-UI
 *
 * Simple fetch-based authentication client that works with our auth API.
 * This replaces Better Auth React client to avoid protocol compatibility issues.
 */

const AUTH_API_URL = "https://admin-auth-api.lancetkeith.workers.dev";
const TOKEN_KEY = "auth_token";

// Auth error handling types
export interface AuthError {
  code: string;
  message: string;
  details?: any;
}

export interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  emailVerified: boolean;
  createdAt: number;
  updatedAt: number;
}

export interface Session extends User {
  token: string;
  expiresAt: number;
}

/**
 * Sign in with email and password
 */
export const signIn = {
  email: async ({
    email,
    password,
  }: {
    email: string;
    password: string;
    callbackURL?: string;
  }): Promise<{ error?: any; data?: Session }> => {
    try {
      const response = await fetch(`${AUTH_API_URL}/api/auth/sign-in/email`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, password }),
        credentials: "include",
      });

      if (!response.ok) {
        const error = await response.json();
        return { error };
      }

      const data = await response.json();

      // Store token in localStorage
      if (data.session?.token) {
        localStorage.setItem(TOKEN_KEY, data.session.token);
      }

      return { data: { ...data.user, ...data.session } };
    } catch (error) {
      return {
        error: {
          code: "NETWORK_ERROR",
          message: "Failed to connect to authentication service",
        },
      };
    }
  },
};

/**
 * Sign out
 */
export const signOut = async (): Promise<void> => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) {
    try {
      await fetch(`${AUTH_API_URL}/api/auth/sign-out`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        credentials: "include",
      });
    } catch {
      // Ignore errors during sign out
    }
  }

  localStorage.removeItem(TOKEN_KEY);
};

/**
 * Get current session
 */
export const getSession = async (): Promise<Session | null> => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (!token) {
    return null;
  }

  try {
    const response = await fetch(`${AUTH_API_URL}/api/auth/get-session`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      credentials: "include",
    });

    if (!response.ok) {
      localStorage.removeItem(TOKEN_KEY);
      return null;
    }

    const data = await response.json();
    if (!data.user) {
      localStorage.removeItem(TOKEN_KEY);
      return null;
    }

    return { ...data.user, ...data.session };
  } catch {
    localStorage.removeItem(TOKEN_KEY);
    return null;
  }
};

/**
 * React hook for session state
 */
export const useSession = () => {
  const [session, setSession] = React.useState<Session | null>(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    getSession().then((s) => {
      setSession(s);
      setLoading(false);
    });
  }, []);

  return { data: session, isPending: loading };
};

// Import React for hooks
import React from "react";

// Utility functions for client-side auth state
export const isAuthenticated = async (): Promise<boolean> => {
  const session = await getSession();
  return !!session;
};

export const hasRole = async (role: string): Promise<boolean> => {
  const session = await getSession();
  return session?.role === role;
};

export const handleAuthError = (error: any): AuthError => {
  if (error?.code) {
    return error as AuthError;
  }

  return {
    code: "UNKNOWN_ERROR",
    message: error?.message || "An unexpected error occurred",
    details: error,
  };
};
